// Helpers shared by server-paged list pages (users, roles): offset math, URL
// query sync for count/page, and a CSV file download built from rows.

export function pageOffset(page, perPage) {
  return Math.max(0, (Number(page) - 1) * Number(perPage))
}

// Replace the route query with the current paging state without adding history
// entries. Extra keys are merged in (filters a page wants bookmarkable).
export function syncPagingQuery(router, route, page, perPage, extra = {}) {
  const query = { ...route.query, ...extra, count: String(perPage), page: String(page) }
  router.replace({ path: route.path, query }).catch((err) => {
    if (err && err.name !== 'NavigationDuplicated') throw err
  })
}

// Read count/page from the route query with sane defaults.
export function readPagingQuery(route, defaultPerPage = 10) {
  const perPage = Number(route.query.count) || defaultPerPage
  const page = Number(route.query.page) || 1
  return { perPage, page }
}

// A cell that starts with = + - @ (or a tab or carriage return) runs as a formula when the
// export is opened in Excel, Numbers or Sheets. Values such as utm parameters come straight
// from public booking links, so a customer could plant one. Prefix those with ' so they open
// as text. Plain negative numbers and amounts ("-5", "-$12.50", "-12,50 kr") and a lone "-"
// stay as they are; anything with an operator or a formula after the minus is prefixed.
export function neutralizeCsvFormula(value) {
  if (typeof value !== 'string' || value === '' || value === '-') return value
  if (/^[=+@\t\r]/.test(value)) return "'" + value
  if (value.charAt(0) === '-' && !/^-[^\d=+\-*/^@()&|!<>;"]{0,4}\d[\d.,\s]*[^\d=+\-*/^@()&|!<>;"]{0,4}$/.test(value)) {
    return "'" + value
  }
  return value
}

function csvEscape(value) {
  return `"${neutralizeCsvFormula(String(value == null ? '' : value)).replace(/"/g, '""')}"`
}

// columns: [{ label, value: (row) => any }]
export function downloadCsv(filename, columns, rows) {
  let csv = columns.map((c) => csvEscape(c.label)).join(',') + '\n'
  rows.forEach((row) => {
    csv += columns.map((c) => csvEscape(c.value(row))).join(',') + '\n'
  })
  const anchor = document.createElement('a')
  anchor.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv)
  anchor.target = '_blank'
  anchor.download = filename
  anchor.click()
}

// Fetch every page of a paged endpoint. fetchPage(count, offset) must resolve to
// { rows, total }. Batches of 100, stops on a short page or when total is reached.
export async function fetchAllPages(fetchPage, batch = 100) {
  const all = []
  let offset = 0
  for (;;) {
    const { rows, total } = await fetchPage(batch, offset)
    all.push(...rows)
    offset += batch
    if (rows.length === 0 || all.length >= (total || 0)) break
  }
  return all
}
